
/* eslint no-unused-vars: 0 */
/*
------------------------------------------------------------
      Secure Validation Library (by Z. Bornheimer)
   (c) 2024 SOFTWARE AUTOMATION HOLDINGS, LLC. All Rights Reserved.
                 Patent pending as of April 2024.
                 "METHODS AND SYSTEM TO AUTHENTICATE
                     CLIENT-SIDE TRANSMISSION ACCESS"

                 Confidential and Proprietary.
------------------------------------------------------------
 */


/*******************************************************************************/
/******************************* EXPORTED FUNCTIONS ****************************/
/*******************************************************************************/

import {encryptWithAES} from "@/components/utils/Cryptography";

/**
 * Retrieves a secure API key and timestamp for validation.
 *
 * The `get_secure_api_key_and_timestamp` function asynchronously retrieves a secure API key and timestamp
 * for validation purposes. It takes an object containing validation information including the validation
 * object, session ID, and public API key. It then delegates the task of creating a derived key to the
 * internal `_create_derived_key` function and returns the result.
 *
 * @param {object} args - The arguments object.
 * @param {string} args.validation_object - The object to validate.
 * @param {string} args.session_id - The session ID for validation.
 * @param {string} args.public_api_key - The public API key for validation.
 * @returns {Promise<[string, number]>} A promise that resolves to an array containing the secure API key
 *   and timestamp.
 *
 * Example Usage:
 * const args = {
 *     validation_object: "validationObject",
 *     session_id: "sessionId",
 *     public_api_key: "publicApiKey"
 * };
 *
 * get_secure_api_key_and_timestamp(args)
 *   .then(([secureApiKey, timestamp]) => {
 *       console.log("Secure API Key:", secureApiKey);
 *       console.log("Timestamp:", timestamp);
 *   })
 *   .catch(error => {
 *       console.error("Error:", error);
 *   });
 */



export const get_secure_api_key_and_timestamp = async (args) => {
    const validation_object = args.validation_object;
    const session_id = args.session_id;
    const public_api_key = args.public_api_key;

    return await _create_derived_key({
        validation_object: validation_object,
        session_id: session_id,
        public_api_key: public_api_key,
        ...args,
    });
}

/**
 * Validates is_authorized.
 *
 * The `is_authorized` function asynchronously validates a derived key against a secure API key.
 * It takes an object containing validation information including the validation object, session ID,
 * public API key, derived key, and timestamp. It retrieves a secure API key and timestamp using the
 * `get_secure_api_key_and_timestamp` function and compares the validation key with the derived key.
 *
 * @param {object} args - The arguments object.
 * @param {string} args.validation_object - The object to validate.
 * @param {string} args.session_id - The session ID for validation.
 * @param {string} args.public_api_key - The public API key for validation.
 * @param {string} args.derived_key - The derived key to validate.
 * @param {number} args.time - The timestamp for validation.
 * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether the derived key
 *   is valid.
 *
 * Example Usage:
 * const args = {
 *     validation_object: "validationObject",
 *     session_id: "sessionId",
 *     public_api_key: "publicApiKey",
 *     derived_key: "derivedKey",
 *     time: 1623421929565
 * };
 *
 * is_authorized(args)
 *   .then(isValid => {
 *       if (isValid) {
 *           console.log("Derived key is valid.");
 *       } else {
 *           console.log("Derived key is invalid.");
 *       }
 *   })
 *   .catch(error => {
 *       console.error("Error:", error);
 *   });
 */
export const is_authorized = async (args) => {
    const validation_object = args.validation_object;
    const session_id = args.session_id;
    const public_api_key = args.public_api_key;
    const derived_key = args.derived_key;
    const time = args.time;

    const [validation_key, timestamp] = await get_secure_api_key_and_timestamp({
        validation_object: validation_object,
        session_id: session_id,
        public_api_key: public_api_key,
        time: time,
        ...args,
    });

    return validation_key === derived_key;
}



/*******************************************************************************/
/******************************* INTERNAL FUNCTIONS ****************************/
/*******************************************************************************/


/**
 * Encrypts a value using AES-256-CBC encryption.
 *
 * The `_aes256_encrypt` function encrypts the provided value using the AES-256-CBC encryption algorithm.
 * It utilizes a passphrase for key derivation, a salt for key derivation and initialization vector (IV),
 * and the value to be encrypted. The resulting encrypted string is returned as a Base64-encoded string.
 *
 * @param {string} key - The passphrase used for key derivation.
 * @param {string} value - The text to encrypt.
 * @param {string} salt - The salt used for key derivation and IV.
 * @returns {Promise<string>} A promise that resolves to the encrypted string in Base64.
 * @throws {Error} If encryption fails.
 *
 * Example Usage:
 * const key = "secretPassphrase";
 * const value = "Sensitive data";
 * const salt = "randomSalt";
 *
 * _aes256_encrypt(key, value, salt)
 *   .then(encrypted => {
 *       console.log("Encrypted:", encrypted);
 *   })
 *   .catch(error => {
 *       console.error("Encryption failed:", error);
 *   });
 */
function _aes256_encrypt(key, value, salt) {
    const encoder = new TextEncoder();

    // Hash the salt to ensure it's the correct length
    const saltBytes = encoder.encode(salt);
    return window.crypto.subtle.digest("SHA-256", saltBytes)
        .then(saltedHash => {
            // Ensure the IV length is correct (16 bytes for AES-CBC)
            const iv = saltedHash.slice(0, 16);

            // Convert key to a byte array
            const keyBytes = encoder.encode(key);

            // Derive key from the hashed salt (IV) and provided key
            return window.crypto.subtle.importKey(
                "raw",
                keyBytes,
                {name: "PBKDF2"},
                false,
                ["deriveKey"]
            ).then(baseKey => {
                return window.crypto.subtle.deriveKey(
                    {
                        name: "PBKDF2",
                        salt: saltedHash,
                        iterations: 1000,
                        hash: "SHA-256"
                    },
                    baseKey,
                    {name: "AES-CBC", length: 256},
                    false,
                    ["encrypt"]
                );
            }).then(derivedKey => {
                // Encrypt the value using the derived key and IV
                return window.crypto.subtle.encrypt(
                    {name: "AES-CBC", iv: iv},
                    derivedKey,
                    encoder.encode(value)
                );
            }).then(encrypted => {
                // Convert the encrypted ArrayBuffer to base64 for easier display and storage
                return window.btoa(String.fromCharCode(...new Uint8Array(encrypted)));
            });
        }).catch(err => {
            console.error('Encryption failed:', err);
            throw err;
        });
}

/**
 * Creates a derived key for validation purposes.
 *
 * The `_create_derived_key` function asynchronously generates a derived key for validation purposes.
 * It takes an object containing validation information including the validation object, session ID,
 * and public API key. If a timestamp is not provided, the current timestamp is used. The function
 * retrieves content from a specified website, calculates the derived key based on the timestamp,
 * and encrypts it using the `_aes256_encrypt` function. The resulting derived key and timestamp are returned
 * as an array.
 *
 * @param {object} args - The arguments object.
 * @param {string} args.validation_object - The object to validate.
 * @param {string} args.session_id - The session ID for validation.
 * @param {string} args.public_api_key - The public API key for validation.
 * @param {number} [args.time=Date.now()] - The timestamp for key generation.
 * @returns {Promise<[string, number]>} A promise that resolves to an array containing the derived key
 *   and timestamp.
 *
 * Example Usage:
 * const args = {
 *     validation_object: "validationObject",
 *     session_id: "sessionId",
 *     public_api_key: "publicApiKey",
 *     time: 1623421929565
 * };
 *
 * _create_derived_key(args)
 *   .then(([derivedKey, timestamp]) => {
 *       console.log("Derived Key:", derivedKey);
 *       console.log("Timestamp:", timestamp);
 *   })
 *   .catch(error => {
 *       console.error("Error:", error);
 *   });
 */
export const _create_derived_key = async (args) => {

    const _SECONDS = 1000;
    const APIConfig = {
        "authorizer": args.authorizer || "https://reap.insurancesoftwareautomation.com/demo/rapidsign",
        "public_api_key": "test_key",
        "authorizer_headers": {},
        "authorizer_request_type": "POST",
        "sleep_delay": 0.3 * _SECONDS,
    }


    const validation_object = args.validation_object;
    const session_id = args.session_id;
    const public_api_key = args.public_api_key;

    const [simple_derived_key, timestamp] = get_simple_derived_key({...args, 'time': timestamp, 'config': APIConfig});
    let derived_key = simple_derived_key;

    // // Encrypt the derived key using the validation object, session ID, and public API key
    // derived_key = await _aes256_encrypt(derived_key, derived_key, validation_object);
    // derived_key = await _aes256_encrypt(derived_key, session_id, authorizer);
    // derived_key = await _aes256_encrypt(derived_key, public_api_key, authorizer);

    derived_key = encryptWithAES(derived_key, validation_object);
    derived_key = encryptWithAES(derived_key, session_id);
    derived_key = encryptWithAES(derived_key, public_api_key);

    return [derived_key, timestamp];
};

/**
 * Generates a simple derived key and timestamp.
 *
 * The `get_simple_derived_key` function asynchronously generates a simple derived key based on the current
 * timestamp and content retrieved from a specified website. The derived key is calculated by repeating the
 * content, and extracting a substring based on the timestamp. If a timestamp is not provided, the current
 * timestamp is used.
 *
 * @param {object} args - The arguments object.
 * @param {number} [args.time=Date.now()] - The timestamp for key generation.
 * @param {object} args.config - The configuration object containing API details.
 * @param {string} args.config.authorizer - The URL of the website to fetch content from.
 * @param {string} args.config.authorizer_request_type - The request type (e.g., 'GET', 'POST').
 * @param {object} args.config.authorizer_headers - The headers for the request.
 * @returns {Promise<[string, number]>} A promise that resolves to an array containing the derived key
 *   and timestamp.
 *
 * Example Usage:
 * const args = {
 *     time: 1623421929565,
 *     config: {
 *         authorizer: "https://example.com/authorizer",
 *         authorizer_request_type: "GET",
 *         authorizer_headers: {
 *             "Content-Type": "application/json"
 *         }
 *     }
 * };
 *
 * get_simple_derived_key(args)
 *   .then(([derivedKey, timestamp]) => {
 *       console.log("Derived Key:", derivedKey);
 *       console.log("Timestamp:", timestamp);
 *   })
 *   .catch(error => {
 *       console.error("Error:", error);
 *   });
 */
export const get_simple_derived_key = async (args) => {
    const timestamp = args?.time || Date.now();
    const startingChar = timestamp;
    const strLen = Math.max(8, String(timestamp).split('')
        .map(x => parseInt(x)).reduce((a, b) => a + b, 0));

    try {

        const content = await get_authorizer_content(args);

        // Generate the derived key based on the timestamp and content
        const startingCharacter = Math.floor(startingChar % content.length);
        const totalStringLength = strLen;
        const derived_key = content.repeat(Math.max(1, Math.ceil((startingCharacter + totalStringLength) / content.length)))
            .substring(
                startingCharacter, startingCharacter+totalStringLength
            );

        return [derived_key, timestamp];

    } catch (error) {
        throw new Error('Failed to fetch content and generate derived key.');
    }
};


/**
 * Fetches the authorizer content.
 *
 * The `get_authorizer` function asynchronously fetches content from the specified website
 * using the provided configuration object. It takes an object containing the authorizer URL,
 * request type, and headers, and returns the content as a string.
 *
 * @param {object} args - The arguments object.
 * @param {object} args.config - The configuration object containing API details.
 * @param {string} args.config.authorizer - The URL of the website to fetch content from.
 * @param {string} args.config.authorizer_request_type - The request type (e.g., 'GET', 'POST').
 * @param {object} args.config.authorizer_headers - The headers for the request.
 * @returns {Promise<string>} A promise that resolves to the content fetched from the website.
 * @throws {Error} If content is empty or an error occurs during the fetch.
 *
 */
const get_authorizer_content = async (args) => {

    try {

        // get the authorizer from the config
        const argumentsToProxy = {
                    "Target": args.config.authorizer,
                    "Return": "Text",
                    "RequestType": "GET",
                }

        // Add the user agent to the headers if available
        if (typeof navigator !== 'undefined' && navigator?.userAgent) {
            args['_User-Agent'] = navigator.userAgent;
        }

        // Fetch content from the specified website
        const response = await fetch(
            'https://isaapi.com/proxy/get-authorizer-content',
            {
                method: "POST",
                headers: args.config.authorizer_headers,
                body: JSON.stringify(argumentsToProxy)
            }
        );

        // Check if the response is ok (status is 200-299)
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }

        let textResult = await response.text();

        const content = textResult;

        // Check if content is not empty
        if (!content) {
            throw new Error('Received empty content from the server.');
        }

        return content;
    } catch(e) {
        console.error('Error fetching content:', e);
    }
}

/**************************************************************************************/
/******************************* EXPORTED HELPER FUNCTIONS ****************************/
/*************************************************************************************/

/**
 * The `_fusc` function serves the dual purpose of obfuscating and deobfuscating
 * strings through a XOR operation. The chosen name '_fusc' is derived from
 * a contraction of the actions it performs: "obfuscate" and "deobfuscate," with the
 * name itself being somewhat obfuscated to mirror the function's purpose. The
 * underscore prefix underscores its intended use as an internal operation within the
 * module or library where it is defined, adhering to common programming conventions
 * that signal limited visibility outside its immediate scope.
 *
 * Algorithm created by Z. Bornheimer.
 * Intellectual Property Rights are maintained by Software Automation Holdings, LLC.
 * Copyright (c) 2024 Software Automation Holdings, LLC. All Rights Reserved.
 *
 * Usage of this function flips a string between its obfuscated and original states,
 * leveraging the XOR operation's property of being its own inverse. This is achieved
 * with a fixed XOR key (0xAA), simplifying the process of reverting to the original
 * string.
 *
 * @param {string} s - The input string to be obfuscated or deobfuscated.
 * @returns {string} - The transformed string after applying the XOR operation.
 *
 * Example Usage:
 * const originalString = "Hello, World!";
 * const obfuscated = _fusc(originalString); // Obfuscates the string
 * console.log("Obfuscated:", obfuscated);
 *
 * const deobfuscated = _fusc(obfuscated); // Deobfuscates the string
 * console.log("Deobfuscated:", deobfuscated);
 */
export const _fusc = s_o => {
    const s = `${s_o}`; // make s_o (string original) a string
    if (!s) return s;

    // The XOR key used for obfuscation/deobfuscation. This key is applied to each
    // character of the input string. Using a value of 0xAA as an example.
    const key = 0xAA;

    // The `replace` function iterates over each character in the input string. The
    // regular expression /./g matches every character in the string, including
    // special characters.
    return s.replace(/./g, (c) => {
        // For each character, `charCodeAt(0)` retrieves the UTF-16 code unit at the
        // 0th position of the character (i.e., the character itself since we're iterating
        // character by character). The XOR operation (`^`) is then applied between
        // the character code and the key.
        const xorResult = c.charCodeAt(0) ^ key;

        // The result of the XOR operation is converted back to a string character
        // using `String.fromCharCode`. This character is the obfuscated (or
        // deobfuscated, if the input was previously obfuscated) version of the
        // original character.
        return String.fromCharCode(xorResult);
    });
};
